export type SortCol = "countryName" | "store" | "country" | "coworker";
export type ExportRoleType =
  "store" | "country" | "both" | "coworker" | "coworker-and-store";

export interface AtkomstUser {
  email: string;
  roles: string[];
  nodeRefs: string[];
}

// Atkomst uses "Fixa Super User" for both store and country SUs.
// Distinguish them by the node type the user was fetched from.
const STORE_SUPER_USER_ROLE = "Fixa Super User";
const COUNTRY_SUPER_USER_ROLE = "Fixa Country Super User"; // fallback for any config that uses this name
const COWORKER_ROLE = "Fixa Coworker";

export const PAGE_SIZE = 50;

const EXPORT_ROLE_FILE_SEGMENT: Record<ExportRoleType, string> = {
  store: "store-super-users",
  country: "country-super-users",
  both: "store-and-country-super-users",
  coworker: "coworkers",
  "coworker-and-store": "coworkers-and-store-super-users",
};

export function isStoreSU(
  user: Pick<AtkomstUser, "roles" | "nodeRefs">
): boolean {
  return (
    user.roles.includes(STORE_SUPER_USER_ROLE) &&
    user.nodeRefs.some((ref) => ref.startsWith("STO."))
  );
}

export function isCountrySU(
  user: Pick<AtkomstUser, "roles" | "nodeRefs">
): boolean {
  return (
    (user.roles.includes(STORE_SUPER_USER_ROLE) &&
      user.nodeRefs.some((ref) => ref.startsWith("RU."))) ||
    user.roles.includes(COUNTRY_SUPER_USER_ROLE)
  );
}

export function isCoworker(user: Pick<AtkomstUser, "roles">): boolean {
  return user.roles.includes(COWORKER_ROLE);
}

export function escapeCsvField(value: string): string {
  // Mitigate CSV injection in spreadsheet apps (e.g. Excel) by neutralizing formula prefixes.
  const safe = /^[=+\-@]/.test(value) ? `'${value}` : value;
  if (/[,"\r\n]/.test(safe)) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}

export function buildExportFileName(exportRole: ExportRoleType): string {
  const now = new Date();
  const pad2 = (n: number) => String(n).padStart(2, "0");
  const pad3 = (n: number) => String(n).padStart(3, "0");
  const datePart = `${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}`;
  const timePart = `${pad2(now.getHours())}${pad2(now.getMinutes())}${pad2(now.getSeconds())}${pad3(now.getMilliseconds())}`;
  return `atkomst-users-${EXPORT_ROLE_FILE_SEGMENT[exportRole]}-${datePart}-${timePart}.csv`;
}

export function formatNodeLabel(nodeRef: string, storeName?: string): string {
  const [type, id] = nodeRef.split(".");
  if (type === "RU") return `Country ${id}`;
  return storeName ? `${id} — ${storeName}` : `Store ${id}`;
}

export function getUserCountryNames(
  user: Pick<AtkomstUser, "nodeRefs">,
  countryNameById: Map<string, string>,
  storeRefToCountryId: Map<string, string>
): string[] {
  const names = new Set<string>();
  user.nodeRefs.forEach((ref) => {
    if (ref.startsWith("RU.")) {
      const countryName = countryNameById.get(ref.slice(3));
      if (countryName) names.add(countryName);
      return;
    }
    if (ref.startsWith("STO.")) {
      const countryId = storeRefToCountryId.get(ref);
      if (!countryId) return;
      const countryName = countryNameById.get(countryId);
      if (countryName) names.add(countryName);
    }
  });
  return Array.from(names).sort((a, b) => a.localeCompare(b));
}
